import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { gaEvent } from '../../../utils/google-analytics';
import {
  BalancesDataContainer,
  BalancesHeaderContainer,
  CurrentDueBalancesContainer,
  CurrentDueBalancesData,
  CurrentDueBalancesHeader,
  CurrentDueBalancesHeaderMobile,
  CurrentPayPlanHeader,
  DueDateBalancesContainer,
  DueDateBalancesHeader,
  DueDateBalancesHeaderMobile,
  HidePaymentArea,
  NewPaymentActionContainer,
  PaymentInfo,
  PaymentScheduleButton,
  PaymentScheduleHideButton,
  PayoffAmountBalancesContainer,
  PayoffAmountBalancesData,
  PayoffAmountBalancesHeader,
  PayoffAmountBalancesHeaderMobile,
  PayPlanPayInformation,
  PayPlanScheduleContainer,
  PolicyNumberBalancesHeader,
  PolicyNumberContainer,
  PolicyNumberData,
  PolicyNumberHeaderMobile,
  ShowPaymentContainer,
} from './styles';
import { collapseEventBody, expandEventBody } from './utils';
import EditPayMethod from './editPayMethod';
import { resetStepTwoState } from '../make-payment/step-two-amount/makePaymentStepTwoReducer';
import { getPayPlansForStatementAccounts, getStatementAccount } from './policyAPI';
import { setLoading, setNewPlanSchedule } from './payPlanReducer';
import CancelSaveContainerStatementAccount from './cancelSaveContainerStatementAccount';
import { ga4Event } from '../../../utils/google-analytics-4';
import { useSnackbar } from 'notistack';
import EditStatementPaySchedule from './editStatementPaySchedule';
import ChangePaySchedule from './account-row-components/changePaySchedule';
import ChangePayMethod from './account-row-components/changePayMethod';

export const StatementAccountRow = (props) => {
  const {
    dispatch,
    editPayPlan,
    account,
    accountNumber,
    initialState,
    showPaymentSchedule,
    setShowPaymentSchedule,
    showEditPaymentSchedule,
    setShowEditPaymentSchedule,
    showEditPaymentMethod,
    setShowEditPaymentMethod,
  } = props;
  const { fullAccount } = account;
  let underlyingSA = fullAccount.underlyingBillingAccounts.find(
    (account) => account.systemId === accountNumber,
  );
  const { enqueueSnackbar } = useSnackbar();
  const getPayPlanOptionsAndShowSchedule = () => {
    dispatch(setNewPlanSchedule(null));
    dispatch(resetStepTwoState());
    setShowPaymentSchedule({
      ...initialState,
      [fullAccount.id]: !showPaymentSchedule[fullAccount.id],
    });
    gaEvent(expandEventBody);
    ga4Event(expandEventBody);
    dispatch(setLoading(true));
    dispatch(getStatementAccount(account.fullAccount.statementAccountRef, enqueueSnackbar));
    dispatch(
      getPayPlansForStatementAccounts(
        account.fullAccount.statementAccountRef,
        enqueueSnackbar,
        account?.fullAccount?.underlyingBillingAccounts[1]?.payPlanCd, // full payment plan code instead of truncated version
      ),
    );
    dispatch(setLoading(false));
  };

  return (
    <>
      <BalancesHeaderContainer>
        <PolicyNumberBalancesHeader>Policy Number</PolicyNumberBalancesHeader>
        <PayoffAmountBalancesHeader>Payoff Amount</PayoffAmountBalancesHeader>
        <CurrentDueBalancesHeader>Current Due</CurrentDueBalancesHeader>
        <DueDateBalancesHeader>Payment Schedule</DueDateBalancesHeader>
      </BalancesHeaderContainer>
      <BalancesDataContainer key={fullAccount.id}>
        <PolicyNumberContainer>
          <PolicyNumberHeaderMobile>Policy Number</PolicyNumberHeaderMobile>
          <PolicyNumberData>{fullAccount.accountDisplayNumber}</PolicyNumberData>
        </PolicyNumberContainer>
        <PayoffAmountBalancesContainer>
          <PayoffAmountBalancesHeaderMobile>Payoff Amount</PayoffAmountBalancesHeaderMobile>
          <PayoffAmountBalancesData>
            ${parseFloat(fullAccount.totalAmt).toFixed(2)}
          </PayoffAmountBalancesData>
        </PayoffAmountBalancesContainer>
        <CurrentDueBalancesContainer>
          <CurrentDueBalancesHeaderMobile>Current Due</CurrentDueBalancesHeaderMobile>
          <CurrentDueBalancesData>
            ${parseFloat(fullAccount.openAmt).toFixed(2)}
          </CurrentDueBalancesData>
        </CurrentDueBalancesContainer>
        <DueDateBalancesContainer>
          <DueDateBalancesHeaderMobile>Payment Schedule</DueDateBalancesHeaderMobile>
          {fullAccount.payPlan.schedule && showPaymentSchedule[fullAccount.id] === false ? (
            <ShowPaymentContainer
              onClick={() => {
                getPayPlanOptionsAndShowSchedule();
              }}
            >
              <KeyboardArrowRightIcon />
              <PaymentScheduleButton
                aria-label={`show/hide payment plan for ${fullAccount.accountDisplayNumber}`}
                $accountNumber={fullAccount.accountDisplayNumber}
              >
                {editPayPlan && `Manage Payment Schedule`}
              </PaymentScheduleButton>
            </ShowPaymentContainer>
          ) : (
            <HidePaymentArea
              onClick={() => {
                gaEvent(collapseEventBody);
                ga4Event(collapseEventBody);
                setShowPaymentSchedule({
                  ...showPaymentSchedule,
                  [fullAccount.id]: !showPaymentSchedule[fullAccount.id],
                });
              }}
            >
              <KeyboardArrowDownIcon />
              <PaymentScheduleHideButton>Hide Payment Schedule</PaymentScheduleHideButton>
            </HidePaymentArea>
          )}
        </DueDateBalancesContainer>

        {showPaymentSchedule[fullAccount.id] === true ? (
          <PayPlanScheduleContainer>
            <CurrentPayPlanHeader>{fullAccount.payPlanCd}</CurrentPayPlanHeader>
            {fullAccount.electronicPaymentSource[0].methodCd &&
              !fullAccount?.underlyingBillingAccounts[0]?.payPlan?.payPlanTypeCd.includes(
                'None',
              ) && (
                <PayPlanPayInformation>
                  This pay plan uses{' '}
                  <PaymentInfo>
                    {fullAccount.electronicPaymentSource[0].methodCd} -{' '}
                    {fullAccount.electronicPaymentSource[0].methodCd === 'ACH'
                      ? fullAccount.electronicPaymentSource[0].achBankAccountNumber.slice(-4)
                      : fullAccount.electronicPaymentSource[0].creditCardNumber}{' '}
                  </PaymentInfo>
                  as a payment method.{' '}
                </PayPlanPayInformation>
              )}
            {parseFloat(fullAccount.totalAmt) > 0 && editPayPlan === true && (
              <>
                {!underlyingSA.payPlan.payPlanTypeCd.includes('None') && (
                  <ChangePayMethod
                    account={fullAccount}
                    setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                    setShowEditPaymentMethod={setShowEditPaymentMethod}
                    showEditPaymentMethod={showEditPaymentMethod}
                    initialState={initialState}
                  />
                )}
                <ChangePaySchedule
                  account={fullAccount}
                  initialState={initialState}
                  showEditPaymentSchedule={showEditPaymentSchedule}
                  setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                  setShowEditPaymentMethod={setShowEditPaymentMethod}
                />
                {showEditPaymentSchedule[fullAccount.id] === true ? (
                  <EditStatementPaySchedule account={account} />
                ) : null}
                {showEditPaymentMethod[fullAccount.id] === true &&
                !underlyingSA?.payPlan?.payPlanTypeCd.includes('None') ? (
                  <EditPayMethod account={account} />
                ) : null}
                <NewPaymentActionContainer>
                  {showEditPaymentSchedule[fullAccount.id] === true ||
                  showEditPaymentMethod[fullAccount.id] === true ? (
                    <CancelSaveContainerStatementAccount
                      showEditPaymentMethod={showEditPaymentMethod}
                      setShowPaymentSchedule={setShowPaymentSchedule}
                      showPaymentSchedule={showPaymentSchedule}
                      setShowEditPaymentMethod={setShowEditPaymentMethod}
                      setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                      account={account}
                      underlyingSA={underlyingSA}
                      initialState={initialState}
                      showEditPaymentSchedule={showEditPaymentSchedule}
                    />
                  ) : null}
                </NewPaymentActionContainer>
              </>
            )}
          </PayPlanScheduleContainer>
        ) : null}
      </BalancesDataContainer>
    </>
  );
};
StatementAccountRow.propTypes = {
  account: PropTypes.shape({
    systemId: PropTypes.any,
    fullAccount: PropTypes.shape({
      id: PropTypes.string,
      underlyingBillingAccounts: PropTypes.array,
      electronicPaymentSource: PropTypes.any,
      accountDisplayName: PropTypes.string,
      accountDisplayNumber: PropTypes.any,
      accountNumber: PropTypes.string,
      totalAmt: PropTypes.string,
      openAmt: PropTypes.string,
      payPlanCd: PropTypes.any,
      payPlan: PropTypes.shape({
        schedule: PropTypes.array,
        payPlanTypeCd: PropTypes.string,
      }),
      statementAccountRef: PropTypes.any,
    }),
  }),
  accountNumber: PropTypes.any,
  dispatch: PropTypes.any,
  editPayPlan: PropTypes.bool,
  dueDate: PropTypes.string,
  newPlan: PropTypes.any,
  payPlanDateToPay: PropTypes.any,
  policies: PropTypes.any,
  payPlanTypesToFilter: PropTypes.any,
  initialState: PropTypes.any,
  showPaymentSchedule: PropTypes.any,
  setShowPaymentSchedule: PropTypes.any,
  showEditPaymentSchedule: PropTypes.any,
  setShowEditPaymentSchedule: PropTypes.any,
  showEditPaymentMethod: PropTypes.any,
  setShowEditPaymentMethod: PropTypes.any,
};
const mapStateToProps = (state) => ({
  editPayPlan: state.configurationSlice.features.features.editPayPlan,
  policies: state.policySlice.policies,
  payPlanTypesToFilter: state.configurationSlice.features.features.payPlanTypesToFilter,
});
export default connect(mapStateToProps)(StatementAccountRow);
